import { useQuery, useQueryClient } from 'react-query';
import { fetchWrapper, handleResponse } from 'utils/fetchWrapper';
import { STATUS_TAGS } from 'constants/common';
import { useMemoryStatusTypes } from 'hooks/EntityTypes/useMemoryStatusTypes';
import type { SingleMemoryResponse } from 'types/Memory';
import { useStatusBarStore } from 'stores';

const getMemoryQueueJob = (memoryID: string | undefined | null) => {
  return fetchWrapper<SingleMemoryResponse, true>({
    url: `memories/queue-jobs/${memoryID}`,
    method: 'GET',
    rawResponse: true,
  });
};

export function useMemoriesQueueJob(
  memoryID: string | undefined | null,
  callback = () => {}
) {
  const queryClient = useQueryClient();
  const { memoryStatusTypeIdByNameMap } = useMemoryStatusTypes();
  const updateStatues = useStatusBarStore((state) => state.updateStatues);
  const updatePercentage = useStatusBarStore((state) => state.updatePercentage);

  return useQuery(
    ['memoriesQueueJob', memoryID],
    () => getMemoryQueueJob(memoryID),

    {
      onError: (error) => {},
      onSuccess: async (response) => {
        if (!memoryStatusTypeIdByNameMap) {
          return;
        }
        const retryAfter = response.headers.get('Retry-After');
        const data = await handleResponse<SingleMemoryResponse>(response);

        const queueJob = data.data.meta.queueJob;

        const memoryAttributes = data?.data.attributes;
        const memoryStatusTypeID = memoryAttributes.memoryStatusTypeID;
        const percentageComplete =
          queueJob?.data.attributes.percentageComplete || 0;

        const isError =
          memoryStatusTypeID ===
          +memoryStatusTypeIdByNameMap[STATUS_TAGS.ERROR];

        const isRunning =
          memoryStatusTypeID ===
            +memoryStatusTypeIdByNameMap[STATUS_TAGS.IMPORT] ||
          memoryStatusTypeID ===
            +memoryStatusTypeIdByNameMap[STATUS_TAGS.IMPORTING];

        const isPending =
          memoryStatusTypeID ===
          +memoryStatusTypeIdByNameMap[STATUS_TAGS.PENDING];

        const isComplete = percentageComplete === 100;
        const isAttempted = isRunning || isComplete;
        updateStatues({
          isRunning: isRunning || isPending,
          isComplete,
          isAttempted,
        });
        updatePercentage(percentageComplete);
        if (isComplete) {
          queryClient.invalidateQueries('memories');
          queryClient.invalidateQueries('workspaceRecommendedMemories');
          return;
        }
        if (isError) {
          callback && callback();
          return;
        }
        setTimeout(
          () => {
            queryClient.invalidateQueries(['memoriesQueueJob', memoryID]);
          },
          retryAfter ? parseInt(retryAfter) * 1000 : 1000
        );
      },
      enabled: Boolean(memoryID),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
}
